import { TextPill } from '../FbUI/TextPill'
import { colors } from '../../styles/tokens'

/**
 * Color mappings for sample request status pills.
 *
 * "solid" variant: white text on a bold background (used in preview headers).
 * "subtle" variant: dark text on a muted background (used in tables and cards).
 */
const SOLID_COLORS: Record<string, { bg: string; text: string }> = {
  submitted: { bg: colors['status-submitted'], text: colors['fg-inverse'] },
  pending_review: { bg: colors['warning'], text: colors['fg-inverse'] },
  accepted: { bg: colors['info'], text: colors['fg-inverse'] },
  rejected: { bg: colors['status-rejected'], text: colors['fg-inverse'] },
  cancelled: { bg: colors['status-default-bg'], text: colors['fg-inverse'] },
}

const SUBTLE_COLORS: Record<string, { bg: string; text: string }> = {
  submitted: {
    bg: colors['status-submitted-bg'],
    text: colors['status-submitted-text'],
  },
  cancelled: {
    bg: colors['status-cancelled-bg'],
    text: colors['status-cancelled-text'],
  },
  pending_review: {
    bg: colors['status-pending-bg'],
    text: colors['status-pending-text'],
  },
  accepted: {
    bg: colors['status-accepted-bg'],
    text: colors['status-accepted-text'],
  },
  rejected: {
    bg: colors['status-rejected-bg'],
    text: colors['status-rejected-text'],
  },
}

const DEFAULT_COLOR = {
  bg: colors['status-default-bg'],
  text: colors['status-default-text'],
}

interface SampleStatusBadgeProps {
  status: string
  label: string
  variant?: 'solid' | 'subtle'
  className?: string
  style?: React.CSSProperties
}

/**
 * Reusable status badge for sample requests.
 *
 * @param status - The raw status value (e.g. "submitted", "pending_review")
 * @param label - Display text (e.g. status_display from the API)
 * @param variant - "solid" for bold badges (headers), "subtle" for muted (tables/cards)
 */
export function SampleStatusBadge({
  status,
  label,
  variant = 'subtle',
  className,
  style,
}: SampleStatusBadgeProps) {
  const palette = variant === 'solid' ? SOLID_COLORS : SUBTLE_COLORS
  const color = palette[status] || DEFAULT_COLOR

  return (
    <TextPill
      backgroundColor={color.bg}
      textColor={color.text}
      className={className}
      style={style}
    >
      {label}
    </TextPill>
  )
}
